// Mitarbeitermotivation (Idee Jasmin):
//  - Morgen-Gruss: 1x/Tag beim ersten Oeffnen vor 11 Uhr ("Guten Morgen, <Vorname>!")
//  - Wochen-Recap: Fr-So, 1x/Woche, Konfetti-Popup mit fertigen Auftraegen (Team + persoenlich)
// Rein additiv, faellt bei Fehlern still zurueck (nie ein Fehler-Popup).
import { useEffect, useState } from 'react';
import { PartyPopper, Sun, X } from 'lucide-react';
import { api } from '../lib/api';

const SPRUECHE = [
  'Gutes Handwerk sieht man — danke dafür.',
  'Präzision heute, Ruhe morgen.',
  'Ein starkes Team macht den Unterschied.',
  'Schritt für Schritt zum fertigen Teil.',
  'Qualität ist kein Zufall.',
  'Auf gute Termine und saubere Kanten.',
  'Packen wir es an — der Tag gehört uns.',
  'Jeder saubere Auftrag zählt.',
];

function lsGet(k: string): string | null { try { return localStorage.getItem(k); } catch { return null; } }
function lsSet(k: string, v: string): void { try { localStorage.setItem(k, v); } catch { /* ignore */ } }
function reducedMotion(): boolean {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

// Dezentes Konfetti (Canvas, selbst gebaut — kein externes Lib). Raeumt sich nach ~2.5s auf.
function konfetti(): void {
  if (reducedMotion()) return;
  const cv = document.createElement('canvas');
  cv.setAttribute('aria-hidden', 'true');
  cv.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:70';
  cv.width = window.innerWidth; cv.height = window.innerHeight;
  document.body.appendChild(cv);
  const ctx = cv.getContext('2d');
  if (!ctx) { cv.remove(); return; }
  const cols = ['#38bdf8', '#0ea5e9', '#fbbf24', '#34d399', '#94a3b8'];
  const parts = Array.from({ length: 90 }, () => ({
    x: Math.random() * cv.width,
    y: -20 - Math.random() * cv.height * 0.5,
    vy: 2 + Math.random() * 3.5,
    vx: -1.5 + Math.random() * 3,
    w: 4 + Math.random() * 4,
    h: 7 + Math.random() * 6,
    rot: Math.random() * Math.PI,
    vr: -0.15 + Math.random() * 0.3,
    c: cols[(Math.random() * cols.length) | 0]!,
  }));
  const start = performance.now();
  let raf = 0;
  const tick = (t: number): void => {
    const elapsed = t - start;
    ctx.clearRect(0, 0, cv.width, cv.height);
    const fade = elapsed > 2000 ? Math.max(0, 1 - (elapsed - 2000) / 500) : 1;
    ctx.globalAlpha = fade;
    for (const p of parts) {
      p.x += p.vx; p.y += p.vy; p.rot += p.vr;
      ctx.save();
      ctx.translate(p.x, p.y); ctx.rotate(p.rot);
      ctx.fillStyle = p.c; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }
    if (elapsed < 2500) raf = requestAnimationFrame(tick);
    else { cancelAnimationFrame(raf); cv.remove(); }
  };
  raf = requestAnimationFrame(tick);
}

type Recap = { team: number; teamDelta: number; mine: number; kw: number };

export function MotivationOverlay(): JSX.Element | null {
  const [greet, setGreet] = useState<{ name: string; spruch: string } | null>(null);
  const [recap, setRecap] = useState<Recap | null>(null);

  useEffect(() => {
    const now = new Date();
    const ymd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const day = now.getDay(); // 0=So, 5=Fr, 6=Sa

    // Morgen-Gruss: 1x/Tag, nur vor 11 Uhr (lokale Zeit)
    if (now.getHours() < 11 && !lsGet(`tl_greet_${ymd}`)) {
      lsSet(`tl_greet_${ymd}`, '1');
      api.dashboard.me()
        .then(d => {
          const vorname = (d.user?.name || '').split(' ')[0] || '';
          if (vorname) setGreet({ name: vorname, spruch: SPRUECHE[now.getDate() % SPRUECHE.length]! });
        })
        .catch(() => { /* still */ });
    }

    // Wochen-Recap: nur Fr/Sa/So, 1x/KW
    if (day === 5 || day === 6 || day === 0) {
      api.dashboard.wochenrecap()
        .then(r => {
          const key = `tl_recap_${now.getFullYear()}-${r.kw}`;
          if (lsGet(key)) return;
          lsSet(key, '1');
          setRecap({ team: r.team.thisWeek, teamDelta: r.team.delta, mine: r.mine.thisWeek, kw: r.kw });
          konfetti();
        })
        .catch(() => { /* still */ });
    }
  }, []);

  // Morgen-Gruss automatisch schliessen
  useEffect(() => {
    if (!greet) return;
    const id = setTimeout(() => setGreet(null), 6000);
    return () => clearTimeout(id);
  }, [greet]);

  // Escape schliesst das Recap-Popup
  useEffect(() => {
    if (!recap) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setRecap(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [recap]);

  if (!greet && !recap) return null;

  return (
    <>
      {greet && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 rounded-full bg-slate-900 px-5 py-3 text-white shadow-lg"
        >
          <Sun className="h-5 w-5 text-amber-400 shrink-0" />
          <span className="text-sm">
            <b>Guten Morgen, {greet.name}!</b> <span className="text-slate-300">· {greet.spruch}</span>
          </span>
        </div>
      )}

      {recap && (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
          onClick={() => setRecap(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-[min(90vw,420px)] rounded-2xl bg-white p-8 text-center shadow-2xl ring-1 ring-slate-200"
            onClick={e => e.stopPropagation()}
          >
            <button
              className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
              onClick={() => setRecap(null)}
              aria-label="Schließen"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-sky-100">
              <PartyPopper className="h-7 w-7 text-sky-600" />
            </div>

            <div className="text-2xl font-bold text-slate-900">
              {recap.team} {recap.team === 1 ? 'Auftrag' : 'Aufträge'} fertig
            </div>
            <div className="text-sm text-slate-500">diese Woche (KW {recap.kw})</div>

            {recap.teamDelta > 0 ? (
              <div className="mt-2 text-sm font-medium text-emerald-600">
                +{recap.teamDelta} mehr als letzte Woche 🎉
              </div>
            ) : (
              <div className="mt-2 text-sm font-medium text-slate-500">
                Weiter so — dranbleiben lohnt sich!
              </div>
            )}

            {recap.mine > 0 && (
              <div className="mt-4 rounded-lg bg-slate-50 py-2 text-sm text-slate-600">
                Dein Anteil: <b>{recap.mine}</b>
              </div>
            )}

            <button
              className="mt-6 w-full rounded-xl bg-brand py-2.5 font-semibold text-white transition hover:bg-sky-600"
              onClick={() => setRecap(null)}
            >
              Los geht&apos;s
            </button>
          </div>
        </div>
      )}
    </>
  );
}
